import { motion } from 'framer-motion';
import { RESULT_TYPES } from './questionFormConfig';
import { RESULT_REASON_CONFIG } from './resultReasonConfig';
import SelectableCard from './components/common/SelectableCard';
import CurationCard from './components/common/CureationCard';
import CURATED_SHOPS from '@/mocks/cureatedShops';
import { Link } from 'react-router-dom';

interface QuestionResultProps {
  result: (typeof RESULT_TYPES)[number];
  scoreMap: Record<string, number>;
  answers: Record<string, string | null>;
  onRetry: () => void;
}

const RESULT_REVIEW_ANALYSIS: Record<
  string,
  {
    highlight: string;
    avoid: string;
  }
> = {
  readyMade: {
    highlight:
      '"가격 대비 만족도가 높다"는 후기가 많아요. 구성과 추가 비용을 꼭 함께 비교해보세요.',
    avoid:
      '수선 범위나 원단 옵션이 제한적인 경우가 있어요. 방문 전에 확인해보세요.',
  },

  semiBespoke: {
    highlight:
      '"핏과 가격 밸런스가 좋다"는 후기가 가장 많아요. 가장 안정적으로 추천되는 방식이에요.',
    avoid:
      '샵마다 가봉 횟수와 제작 범위 차이가 꽤 커요. 계약 전에 체크해보세요.',
  },

  bespoke: {
    highlight:
      '"확실히 체형 보정이 다르다"는 후기가 많아요. 원단과 패턴 차이를 직접 느껴보세요.',
    avoid:
      '제작 기간이 길어질 수 있어요. 최소 2~3개월 이상의 여유를 추천드려요.',
  },

  fast: {
    highlight:
      '빠른 제작 일정이 가능한 업체에 대한 만족도가 높아요. 일정 안정성을 우선 확인해보세요.',
    avoid:
      '급하게 진행될수록 가봉 횟수가 줄어드는 경우가 있어요. 수정 가능 범위를 체크하세요.',
  },

  event: {
    highlight:
      '"촬영 구성 만족도가 높다"는 후기가 많아요. 턱시도 대여 옵션을 꼼꼼히 비교해보세요.',
    avoid:
      '촬영 구성 추가 비용이 발생하는 경우가 있어요. 패키지 범위를 꼭 확인하세요.',
  },

  practical: {
    highlight:
      '"실제로 결혼 이후에도 자주 입는다"는 후기가 많아요. 활용도 중심으로 비교해보세요.',
    avoid:
      '행사 전용 디자인은 실제 활용도가 떨어질 수 있어요. 기본 실루엣을 추천드려요.',
  },

  stylish: {
    highlight:
      '"사진이 정말 잘 나온다"는 후기가 많아요. 트렌디한 핏과 디테일 만족도가 높아요.',
    avoid:
      '유행을 많이 타는 스타일은 몇 년 뒤 어색할 수 있어요. 균형을 고려해보세요.',
  },

  classic: {
    highlight:
      '"원단 퀄리티가 확실히 다르다"는 후기가 많아요. 클래식 원단을 직접 만져보세요.',
    avoid:
      '클래식 스타일은 디테일 차이가 중요해요. 실제 착용 핏을 꼭 확인해보세요.',
  },

  minimal: {
    highlight:
      '"불필요한 옵션 없이 만족도가 높다"는 후기가 많아요. 실속 중심 비교가 중요해요.',
    avoid:
      '구성에 따라 포함 범위 차이가 커요. 셔츠/구두 포함 여부를 확인하세요.',
  },
};

const RESULT_SHOPS: Record<
  string,
  {
    name: string;
    area: string;
    price: string;
    tags: string[];
    reviews: number;
  }[]
> = {
  readyMade: [
    {
      name: '포레스트 예복',
      area: '종로',
      price: '70~95만원',
      tags: ['실속 추천', '원단 다양'],
      reviews: 134,
    },
    {
      name: '맥케이 예복',
      area: '강남',
      price: '80~110만원',
      tags: ['가성비', '실용 구성'],
      reviews: 156,
    },
  ],

  semiBespoke: [
    {
      name: '수트룸 청담',
      area: '청담동',
      price: '100~130만원',
      tags: ['상담 만족도 높음', '핏 정평'],
      reviews: 218,
    },
    {
      name: '맥케이 예복',
      area: '강남',
      price: '80~110만원',
      tags: ['밸런스 추천', '촬영 구성 우수'],
      reviews: 156,
    },
  ],

  bespoke: [
    {
      name: '아틀리에 로웰',
      area: '청담',
      price: '250만원 이상',
      tags: ['비스포크 전문', '원단 퀄리티'],
      reviews: 84,
    },
    {
      name: '수트룸 청담',
      area: '청담동',
      price: '100~130만원',
      tags: ['클래식 추천', '체형 보정'],
      reviews: 218,
    },
  ],
};

// const CURATED_SHOPS = [
//   {
//     name: '수트룸 청담',
//     subtitle: '체형 보정과 핏 만족도가 강점인 반수제 전문',
//     area: '청담동',
//     recommendation:
//       '운동 체형과 핏 중요도를 높게 선택하셨기 때문에, 체형 보정 만족도가 높은 이 업체를 우선 추천드려요. 반수제 기준에서도 어깨와 허리 라인 보정이 꼼꼼하다는 후기가 많습니다.',
//     tags: ['체형보정', '무한수정', '반수제전문', '촬영핏', '원단퀄리티'],
//     reviewSnippet: '수정 대응이 꼼꼼하다는 후기가 많아요. 3차 수정까지도 세밀하게 봐주신다는 리뷰가 반복해서 나옵니다.',
//     reviews: 218,
//     image:
//       'https://readdy.ai/api/search-image?query=elegant%20Korean%20wedding%20suit%20tailor%20shop%20interior%20soft%20natural%20light%20cream%20navy%20palette%20minimal%20modern%20furniture%20premium%20fabric%20rolls%20quiet%20luxury%20editorial%20warm%20ambient%20high-end%20menswear%20boutique&width=420&height=315&seq=yb-c1-main&orientation=landscape',
//     reviewImages: [
//       'https://readdy.ai/api/search-image?query=elegant%20Korean%20groom%20in%20premium%20navy%20tuxedo%20soft%20natural%20light%20editorial%20portrait%20minimal%20cream%20background%20luxury%20wedding%20photography&width=120&height=120&seq=yb-c1-r1&orientation=squarish',
//       'https://readdy.ai/api/search-image?query=Korean%20bride%20and%20groom%20wedding%20portrait%20soft%20romantic%20warm%20lighting%20editorial%20style%20luxury%20wedding%20magazine&width=120&height=120&seq=yb-c1-r2&orientation=squarish',
//       'https://readdy.ai/api/search-image?query=premium%20navy%20wedding%20suit%20fabric%20texture%20detail%20close-up%20soft%20gold%20button%20Italian%20wool%20luxury%20menswear%20product%20photography&width=120&height=120&seq=yb-c1-r3&orientation=squarish',
//       'https://readdy.ai/api/search-image?query=modern%20minimal%20wedding%20suit%20showroom%20cream%20walls%20soft%20ambient%20lighting%20premium%20tailor%20shop%20interior%20quiet%20luxury&width=120&height=120&seq=yb-c1-r4&orientation=squarish',
//     ],
//   },
//   {
//     name: '맥케이 예복',
//     subtitle: '가격 대비 구성이 꼼꼼한 반수제 가성비 강자',
//     area: '강남',
//     recommendation:
//       '합리적인 예산과 가격 만족도를 중시하신 분들에게 가장 적합해요. 반수제 기준으로 촬영/본식 세트 구성이 알차고, 추가 비용 발생률이 낮다는 평가를 받고 있습니다.',
//     tags: ['가성비', '촬영구성', '반수제', '세트구성', '상담만족'],
//     reviewSnippet: '상담 만족도가 높다는 리뷰가 많아요. 투명한 견적과 친절한 피팅 상담을 장점으로 꼽는 후기가 많습니다.',
//     reviews: 156,
//     image:
//       'https://readdy.ai/api/search-image?query=premium%20Korean%20wedding%20tuxedo%20fitting%20room%20interior%20warm%20wood%20paneling%20soft%20cream%20walls%20navy%20accents%20elegant%20mirror%20quiet%20luxury%20tailoring%20studio%20editorial&width=420&height=315&seq=yb-c2-main&orientation=landscape',
//     reviewImages: [
//       'https://readdy.ai/api/search-image?query=Korean%20groom%20in%20charcoal%20gray%20bespoke%20suit%20standing%20elegant%20pose%20soft%20natural%20light%20editorial%20wedding%20portrait%20luxury&width=120&height=120&seq=yb-c2-r1&orientation=squarish',
//       'https://readdy.ai/api/search-image?query=wedding%20couple%20holding%20hands%20romantic%20soft%20focus%20warm%20lighting%20editorial%20photography%20luxury%20bridal%20magazine&width=120&height=120&seq=yb-c2-r2&orientation=squarish',
//       'https://readdy.ai/api/search-image?query=bespoke%20suit%20lapel%20detail%20close-up%20stitching%20craftmanship%20soft%20light%20luxury%20menswear%20tailoring%20editorial&width=120&height=120&seq=yb-c2-r3&orientation=squarish',
//       'https://readdy.ai/api/search-image?query=premium%20Korean%20tailor%20shop%20interior%20wooden%20counter%20fabric%20samples%20soft%20warm%20lighting%20cream%20walls%20quiet%20luxury&width=120&height=120&seq=yb-c2-r4&orientation=squarish',
//     ],
//   },
// ];


const fadeInUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
};
export default function QuestionResult({
  result = {    
    "key":"semiBespoke","title":"밸런스형 신랑","recommendation":"반수제","summary":"가장 많은 신랑님들이 선택하는 안정적인 방식이에요."
  },
  scoreMap = {
    "readyMade":7,"semiBespoke":15,"bespoke":5,"practical":2,"stylish":7,"fast":4,"event":4
  },
  answers = {"budget":"budget_low","bodyType":"body_shoulder","usage":"usage_style","schedule":"schedule_short","photo":"photo_yes","style":"style_trendy"},
  onRetry,
}: QuestionResultProps) {
  const reviewAnalysis =
    RESULT_REVIEW_ANALYSIS[result.key] ||
    RESULT_REVIEW_ANALYSIS.semiBespoke;

  const shops = RESULT_SHOPS[result.key] || RESULT_SHOPS.semiBespoke;

  const sortedResults = Object.entries(scoreMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([key]) =>
      RESULT_TYPES.find((v) => v.key === key)
    )
    .filter(Boolean);

  const tags = sortedResults
    .map((v) => v?.title)
    .filter(Boolean);

  const heroImage = `/resources/images/${result.key}.png`;
  const reasonCards = Object.entries(answers).map(([questionKey, answerKey]) => {
      return RESULT_REASON_CONFIG?.[questionKey]?.[answerKey];
    })
  .filter(Boolean);
  console.log(result)
  const renderRecommendationSection = () => {
    if (result.key === 'readyMade') {
      return (
        <>
          <div className="bg-white rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-navy/5 flex items-center justify-center">
                <i className="ri-check-line text-navy text-xs" />
              </div>
              <p className="text-sm font-semibold text-navy">현재 가장 잘 맞는 방향</p>
            </div>
            <p className="text-xs text-navy-light leading-relaxed pl-8">
              <span className="font-semibold text-navy">기성복 + 수선</span> 조합이 가장 현실적입니다.
              예산 부담이 적고, 준비 기간도 짧아요. 촬영용과 본식용을 구분해서 대여하는 방식도 고려해보세요.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-softgold/10 flex items-center justify-center">
                <i className="ri-arrow-up-line text-softgold text-xs" />
              </div>
              <p className="text-sm font-semibold text-navy">조금 더 투자 가능하다면?</p>
            </div>
            <p className="text-xs text-navy-light leading-relaxed pl-8">
              <span className="font-semibold text-navy">반수제</span>로 올리면 핏 만족도가 크게 올라갑니다.
              체형 보정이 가능하고, 원단 선택 범위도 넓어져요. 특히 촬영 비중이 높거나
              직장에서 정장으로 활용할 예정이라면 반수제를 강력히 추천드려요.
            </p>
          </div>
        </>
      );
    }

    if (result.key === 'semiBespoke') {
      return (
        <>
          <div className="bg-white rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-navy/5 flex items-center justify-center">
                <i className="ri-check-line text-navy text-xs" />
              </div>
              <p className="text-sm font-semibold text-navy">현재 가장 잘 맞는 방향</p>
            </div>
            <p className="text-xs text-navy-light leading-relaxed pl-8">
              <span className="font-semibold text-navy">반수제</span>가 가장 균형 잡힌 선택입니다.
              기성복의 합리적인 가격대에 맞춤형 핏 보정이 더해져, 만족도가 가장 안정적이에요.
              예복 준비가 처음이신 경우에도 진입 장벽이 낮습니다.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-softgold/10 flex items-center justify-center">
                <i className="ri-arrow-up-line text-softgold text-xs" />
              </div>
              <p className="text-sm font-semibold text-navy">완성도를 더 원한다면?</p>
            </div>
            <p className="text-xs text-navy-light leading-relaxed pl-8">
              <span className="font-semibold text-navy">수제 / 비스포크</span>로 올리면
              체형 불균형(어깨, 허리 등)까지 세밀하게 보정 가능합니다.
              착용감과 디테일 자유도가 크게 증가하지만, 제작 기간이 4~8주 더 소요되고
              비용도 1.5~2배 증가해요. 일정과 예산에 여유가 있다면 고려해보세요.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-cream-dark flex items-center justify-center">
                <i className="ri-arrow-down-line text-navy text-xs" />
              </div>
              <p className="text-sm font-semibold text-navy">부담이 크다면?</p>
            </div>
            <p className="text-xs text-navy-light leading-relaxed pl-8">
              <span className="font-semibold text-navy">기성복 + 수선</span>으로 내려가도
              평균 만족도는 여전히 높습니다. 수선만으로도 핏을 상당히 개선할 수 있어요.
              다만 체형 보정 범위와 원단 선택 폭은 줄어듭니다.
            </p>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="bg-white rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 rounded-full bg-navy/5 flex items-center justify-center">
              <i className="ri-check-line text-navy text-xs" />
            </div>
            <p className="text-sm font-semibold text-navy">현재 가장 잘 맞는 방향</p>
          </div>
          <p className="text-xs text-navy-light leading-relaxed pl-8">
            <span className="font-semibold text-navy">수제 / 비스포크</span>가 당신에게 맞습니다.
            원단과 디테일, 장인의 완성도를 중시하시는 분에게 가장 높은 만족도를 줍니다.
            패턴을 신규 제작하기 때문에 체형 불균형도 세밀하게 반영돼요.
          </p>
        </div>
        <div className="bg-white rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 rounded-full bg-cream-dark flex items-center justify-center">
              <i className="ri-arrow-down-line text-navy text-xs" />
            </div>
            <p className="text-sm font-semibold text-navy">현실적인 대안도 가능합니다</p>
          </div>
          <p className="text-xs text-navy-light leading-relaxed pl-8">
            <span className="font-semibold text-navy">반수제</span>로 내려가면 비용이 30~40% 감소하고
            일정 안정성이 높아져요. 평균 만족도도 여전히 높은 편입니다.
            다만 체형 보정 범위와 디테일 자유도는 다소 줄어듭니다.
            예복 외에 다른 지출이 많다면 반수제도 충분히 고려할 만합니다.
          </p>
        </div>
      </>
    );
  };
  return (
    <div className="min-h-[100dvh] bg-cream pb-10">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-cream/90 backdrop-blur-md border-b border-cream-dark">
        <div className="flex items-center h-14 px-4 md:px-6 lg:px-8 max-w-[1200px] mx-auto">
          <button
            onClick={onRetry}
            className="w-8 h-8 flex items-center justify-center text-navy cursor-pointer active:scale-90 transition-transform"
            aria-label="뒤로가기"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <h1 className="ml-2 text-sm font-semibold text-navy">분석 결과</h1>
        </div>
      </div>
      
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="px-4 md:px-6 lg:px-8 pt-6 pb-4 max-w-[1200px] mx-auto"
      >
        <div 
          className="overflow-hidden rounded-[30px] bg-white border-cream-dark shadow-sm"
          style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
        >
          <div className="relative aspect-[16/10] overflow-hidden">
            <img
              src={heroImage}
              alt={result.title}
              className="w-full h-full object-cover"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          </div>

          <div className="bg-navy p-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
            <div className="absolute bottom-0 left-0 w-28 h-28 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/3" />

            <p className="text-xs text-softgold mb-3 font-medium tracking-wide">
              분석 완료
            </p>

            <h2 className="font-serif text-3xl leading-relaxed mb-3">
              당신은
              <br />
              <span className="text-softgold">
                {result.title}
              </span>
              <br />
              타입입니다
            </h2>

            <p className="text-sm text-softgold mb-3">
              추천 방향 · {result.recommendation}
            </p>

            <p className="text-xs text-cream-dark leading-relaxed">
              {result.summary}
            </p>
          </div>
        </div>
      </motion.div>
      <motion.div
        {...fadeInUp}
        transition={{ delay: 0.25, duration: 0.4 }}
        className="px-4 md:px-6 lg:px-8 mb-6 max-w-[1200px] mx-auto"
      >
        <h3 className="font-serif text-base text-navy mb-3">
          당신에게 추천하는 최적의 선택
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {renderRecommendationSection()}
        </div>
      </motion.div>
      {/* Tags */}
      {/* <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15, duration: 0.35 }}
        className="px-5 mb-6"
      >
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1.5 rounded-full bg-white border border-cream-dark text-xs font-medium text-navy"
            >
              {tag}
            </span>
          ))}
        </div>
      </motion.div> */}
      
      {/* Analysis Cards */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.35 }}
        className="px-4 md:px-6 lg:px-8 mb-8 max-w-[1200px] mx-auto"
      >
        <h3 className="font-serif text-base text-navy mb-3">
          왜 이런 결과가 나왔을까요?
        </h3>
        <p className="text-xs text-navy-light mb-4 leading-relaxed">
          당신의 답변을 바탕으로 이런 추천이 나왔어요.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg bg-navy/5 flex items-center justify-center">
                <i className="ri-suitcase-line text-navy text-xs" />
              </div>

              <p className="text-sm font-semibold text-navy">
                추천 방향
              </p>
            </div>

            <p className="text-xs text-navy-light pl-9 leading-relaxed">
              {result.recommendation} 중심으로 비교해보시는 걸 추천드려요.
              {result.summary}
            </p>
          </div>
        
          {
            reasonCards.map((card) => {
              return (
                <div className="bg-white rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-lg bg-navy/5 flex items-center justify-center">
                      <i className="ri-suitcase-line text-navy text-xs" />
                    </div>

                    <p className="text-sm font-semibold text-navy">
                      {card.title}
                    </p>
                  </div>

                  <p className="text-xs text-navy-light pl-9 leading-relaxed">
                    {card.description}
                  </p>
                </div>
                // <SelectableCard
                //   key={card.title}
                //   variant="compact"
                //   showCheck={false}
                //   active={false}
                //   option={{
                //     key: card.title,
                //     title: card.title,
                //     desc: card.description,
                //     icon: card.icon,
                //   }}
                //   onClick={() => {}}
                // />
              )
            })
          }
        

        {/* <div className="bg-white rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-navy/5 flex items-center justify-center">
              <i className="ri-time-line text-navy text-xs" />
            </div>

            <p className="text-sm font-semibold text-navy">
              일정 분석
            </p>
          </div>

          <p className="text-xs text-navy-light pl-9 leading-relaxed">
            {answers.schedule === 'schedule_short'
              ? '예식 일정이 가까운 편이라 빠른 진행 가능 여부를 우선 확인해보세요.'
              : answers.schedule === 'schedule_mid'
              ? '가장 안정적으로 예복을 준비하기 좋은 일정이에요.'
              : '가봉과 디테일까지 충분히 조율 가능한 일정이에요.'}
          </p>
        </div> */}

          <div className="bg-white rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg bg-navy/5 flex items-center justify-center">
                <i className="ri-shirt-line text-navy text-xs" />
              </div>

              <p className="text-sm font-semibold text-navy">
                스타일 취향
              </p>
            </div>

            <p className="text-xs text-navy-light pl-9 leading-relaxed">
              {answers.style === 'style_trendy'
                ? '트렌디한 핏과 실루엣을 중요하게 생각하는 타입이에요.'
                : answers.style === 'style_classic'
                ? '유행보다 오래 입을 수 있는 클래식 스타일을 선호하세요.'
                : '결혼 이후 활용도까지 고려하는 실용적인 취향이에요.'}
            </p>
          </div>

          <div className="bg-white rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg bg-navy/5 flex items-center justify-center">
                <i className="ri-camera-line text-navy text-xs" />
              </div>

              <p className="text-sm font-semibold text-navy">
                촬영 스타일
              </p>
            </div>

            <p className="text-xs text-navy-light pl-9 leading-relaxed">
              {answers.photo === 'photo_yes'
                ? '촬영 대여 구성을 함께 비교하는 것이 중요해 보여요.'
                : '본식 이후 활용도까지 고려하는 방향이 잘 맞아 보여요.'}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Review analysis */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.35 }}
        className="px-4 md:px-6 lg:px-8 mb-8 max-w-[1200px] mx-auto"
        style={{display: 'none'}}
      >
        <h3 className="font-serif text-base text-navy mb-3">
          실제 후기 기반 분석
        </h3>

        <div className="bg-white rounded-2xl p-4 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-softgold/10 flex items-center justify-center shrink-0">
              <i className="ri-star-fill text-softgold text-xs" />
            </div>

            <div>
              <p className="text-xs font-semibold text-navy mb-1">
                후기에서 가장 많이 언급된 기준
              </p>

              <p className="text-[11px] text-navy-light leading-relaxed">
                {reviewAnalysis.highlight}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-softgold/10 flex items-center justify-center shrink-0">
              <i className="ri-shield-check-line text-softgold text-xs" />
            </div>

            <div>
              <p className="text-xs font-semibold text-navy mb-1">
                피해야 할 포인트
              </p>

              <p className="text-[11px] text-navy-light leading-relaxed">
                {reviewAnalysis.avoid}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Shops */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45, duration: 0.35 }}
        className="px-4 md:px-6 lg:px-8 mb-8 max-w-[1200px] mx-auto"
      >
        <h3 className="font-serif text-base text-navy mb-4">
          이 기준에 맞는 인증 예복점
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-6">
          {
            CURATED_SHOPS.map((shop, idx) => {
              return (
                <Link to='/yangbok/detail/test' key={idx}>
                  <CurationCard key={idx} {...shop} />
                </Link>
              )
            })
          }
        </div>
      </motion.div>
    </div>
  );
}